import React, { useState, useEffect } from 'react';
import {
  CheckCircle2, Send, ChevronDown, ChevronUp, Zap, FileCode,
  Settings, Film, RefreshCw, Video, Loader, Trash2, CheckSquare,
  Square, AlertCircle, Clock, Play, Pencil, Save, X, RotateCcw,
  ImageIcon
} from 'lucide-react';
import apiClient from '../../lib/apiClient';

// ─── Video player con proxy backend ─────────────────────────────────────────

const VeoVideoPlayer = ({ uri, mimeType = 'video/mp4', label }) => {
  const [blobUrl, setBlobUrl]   = useState(null);
  const [fetching, setFetching] = useState(true);
  const [error, setError]       = useState(null);

  useEffect(() => {
    let objectUrl = null;
    const load = async () => {
      try {
        const response = await apiClient.get('/projects/veo-video-proxy', {
          params: { uri },
          responseType: 'blob'
        });
        objectUrl = URL.createObjectURL(response.data);
        setBlobUrl(objectUrl);
      } catch (e) {
        setError(e.response?.data?.message || e.message || 'Errore download video');
      } finally {
        setFetching(false);
      }
    };
    load();
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [uri]);

  if (fetching) return (
    <div className="flex items-center gap-2 p-3 text-sm text-gray-500">
      <Loader className="w-4 h-4 animate-spin" /> Download {label} in corso...
    </div>
  );
  if (error) return (
    <div className="p-3 text-xs text-red-600 bg-red-50 rounded-lg">{error}</div>
  );
  return (
    <div className="rounded-lg overflow-hidden border border-gray-100">
      <video controls className="w-full max-h-64 bg-black" src={blobUrl}>
        <source src={blobUrl} type={mimeType} />
      </video>
    </div>
  );
};

// ─── Thumbnail immagine con proxy backend ────────────────────────────────────

const VeoImageThumbnail = ({ uri, selected, onClick }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [error, setError]     = useState(false);

  useEffect(() => {
    let objectUrl = null;
    const load = async () => {
      try {
        const response = await apiClient.get('/projects/veo-video-proxy', {
          params: { uri },
          responseType: 'blob'
        });
        objectUrl = URL.createObjectURL(response.data);
        setBlobUrl(objectUrl);
      } catch {
        setError(true);
      }
    };
    load();
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [uri]);

  return (
    <button
      onClick={onClick}
      className={`relative w-16 h-16 rounded-lg overflow-hidden border-2 shrink-0 transition-all ${
        selected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200 hover:border-blue-300'
      }`}
    >
      {blobUrl ? (
        <img src={blobUrl} alt="ref" className="w-full h-full object-cover" />
      ) : error ? (
        <div className="w-full h-full flex items-center justify-center bg-gray-100">
          <ImageIcon className="w-4 h-4 text-gray-400" />
        </div>
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-gray-100">
          <Loader className="w-3 h-3 text-gray-400 animate-spin" />
        </div>
      )}
      {selected && (
        <div className="absolute inset-0 bg-blue-500/20 flex items-center justify-center">
          <CheckCircle2 className="w-5 h-5 text-blue-600 drop-shadow" />
        </div>
      )}
    </button>
  );
};

// ─── Card variante (stato pending) ──────────────────────────────────────────

const PLATFORMS = [
  { key: 'tiktok',    label: 'TikTok',     color: 'bg-black text-white hover:bg-gray-900'         },
  { key: 'instagram', label: 'Instagram',   color: 'bg-pink-600 text-white hover:bg-pink-700'       },
  { key: 'facebook',  label: 'Facebook',    color: 'bg-blue-700 text-white hover:bg-blue-800'       },
];

const VariantCard = ({ variant, selected, onToggle, imageReferences, selectedImage, onSelectImage, selectedPlatform, onSelectPlatform }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={`border-2 rounded-xl transition-all ${selected ? 'border-blue-400 bg-blue-50' : 'border-gray-200 bg-white'}`}>
      <div className="flex items-start gap-3 p-4">
        <button onClick={() => onToggle(variant.variant_id)} className="mt-0.5 shrink-0">
          {selected
            ? <CheckSquare className="w-5 h-5 text-blue-600" />
            : <Square className="w-5 h-5 text-gray-400" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div>
              <span className="text-xs font-mono text-gray-400">{variant.variant_id}</span>
              <h3 className="font-semibold text-gray-800 text-sm mt-0.5">"{variant.title}"</h3>
              <p className="text-xs text-gray-500 mt-0.5">{variant.narrative_character}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-xs bg-gray-100 text-gray-600 rounded-full px-2 py-0.5">
                {variant.jobs?.length || 0} clip
              </span>
              <button
                onClick={() => setExpanded(e => !e)}
                className="text-gray-400 hover:text-gray-600"
              >
                {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Selettore piattaforma (Speech & Delivery) */}
          <div className="mt-3">
            <p className="text-xs font-medium text-gray-500 mb-2">
              Piattaforma social
              {selectedPlatform
                ? <span className="ml-1 font-semibold text-gray-700"> — {PLATFORMS.find(p => p.key === selectedPlatform)?.label}</span>
                : <span className="ml-1 text-gray-400">(usa regole C9.5)</span>}
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              {PLATFORMS.map(p => (
                <button
                  key={p.key}
                  onClick={() => onSelectPlatform(variant.variant_id, selectedPlatform === p.key ? null : p.key)}
                  className={`px-3 py-1 rounded-full text-xs font-semibold transition-all border-2 ${
                    selectedPlatform === p.key
                      ? `${p.color} border-transparent ring-2 ring-offset-1 ring-gray-400`
                      : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Picker immagine di riferimento */}
          {imageReferences?.length > 0 && (
            <div className="mt-3">
              <p className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1">
                <ImageIcon className="w-3 h-3" />
                Immagine di riferimento VEO
                {selectedImage
                  ? <span className="ml-1 text-blue-600">✓ selezionata</span>
                  : <span className="ml-1 text-gray-400">(usa tutte)</span>}
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                {/* Opzione "nessuna preferenza" */}
                <button
                  onClick={() => onSelectImage(variant.variant_id, null)}
                  className={`w-16 h-16 rounded-lg border-2 flex items-center justify-center text-xs text-center shrink-0 transition-all ${
                    !selectedImage ? 'border-blue-500 ring-2 ring-blue-200 bg-blue-50 text-blue-600 font-medium' : 'border-gray-200 hover:border-blue-300 text-gray-400'
                  }`}
                >
                  Auto
                </button>
                {imageReferences.map((ref, idx) => (
                  <VeoImageThumbnail
                    key={ref.uri || idx}
                    uri={ref.uri}
                    selected={selectedImage === ref.uri}
                    onClick={() => onSelectImage(variant.variant_id, selectedImage === ref.uri ? null : ref.uri)}
                  />
                ))}
              </div>
            </div>
          )}

          {expanded && (
            <div className="mt-3 space-y-2">
              <p className="text-xs text-gray-500 italic">Setting: {variant.setting}</p>
              {(variant.jobs || []).map(job => (
                <div key={job.job_id} className="bg-white border border-gray-100 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-mono text-blue-500">{job.job_id}</span>
                    <span className="text-xs text-gray-400">clip {job.order}</span>
                  </div>
                  <p className="text-xs text-gray-700 leading-relaxed line-clamp-3">{job.text}</p>
                  <p className="text-xs text-gray-400 italic mt-1 line-clamp-1">{job.instruction}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Card variante VEO (stato approved) — mostra clip progress ───────────────

// WPM per piattaforma (usato per suggerire i secondi per clip)
const WPM_BY_PLATFORM = { tiktok: 195, instagram: 175, facebook: 145 };

const suggestDuration = (text, platform) => {
  const wpm    = WPM_BY_PLATFORM[(platform || '').toLowerCase()] || 160;
  const words  = text?.trim().split(/\s+/).filter(Boolean).length || 0;
  if (!words) return 8;
  return Math.min(8, Math.max(4, Math.ceil(words / (wpm / 60))));
};

const VariantResultCard = ({ job, variantJobs, onSubmitJob, onExtendJob, onUpdateClip, onDeleteClip, onRegenerateClip }) => {
  const [expanded, setExpanded]       = useState(false);
  const [submitting, setSubmitting]   = useState(false);
  const [extending, setExtending]     = useState(false);
  const [cardError, setCardError]     = useState(null);
  const [editingClip, setEditingClip]         = useState(null);
  const [editText, setEditText]               = useState('');
  const [editInstruction, setEditInstruction] = useState('');
  const [saving, setSaving]                   = useState(false);
  const [confirmDeleteClip, setConfirmDeleteClip]       = useState(null);
  const [deleting, setDeleting]                         = useState(false);
  const [confirmRegenerateClip, setConfirmRegenerateClip] = useState(null);
  const [regenerating, setRegenerating]                 = useState(false);
  const [clipDurations, setClipDurations]               = useState({});

  const clips          = job.clips || [];
  const hasActive      = clips.some(c => c.status === 'in_progress');
  const completedClips = clips.filter(c => c.status === 'completed');
  const failedClips    = clips.filter(c => c.status === 'failed');

  // Il prossimo clip da processare è sempre quello subito dopo l'ultimo completato.
  const nextClipIdx    = completedClips.length;
  const nextClip       = clips[nextClipIdx];
  const nextIsRetry    = nextClip?.status === 'failed';

  const canSubmitFirst = clips[0]?.status === 'queued' && !hasActive;
  const canExtend      = completedClips.length > 0 && nextClip && (nextClip.status === 'queued' || nextClip.status === 'failed') && !hasActive;
  const allDone        = job.status === 'completed';

  const getDuration = (clipIndex) => {
    const val = clipDurations[clipIndex];
    if (val && val >= 5 && val <= 8) return val;
    return suggestDuration(variantJobs?.[clipIndex]?.text, job.platform);
  };

  const handleSubmit = async (e) => {
    e.stopPropagation();
    setSubmitting(true);
    setCardError(null);
    try {
      await onSubmitJob(job.jobId, getDuration(0));
    } catch (err) {
      setCardError(err.message || 'Errore invio');
    } finally {
      setSubmitting(false);
    }
  };

  const handleExtend = async (e) => {
    e.stopPropagation();
    setExtending(true);
    setCardError(null);
    try {
      await onExtendJob(job.jobId, getDuration(nextClipIdx));
    } catch (err) {
      setCardError(err.message || 'Errore estensione');
    } finally {
      setExtending(false);
    }
  };

  const handleStartEdit = (clipIndex, currentText, currentInstruction) => {
    setEditingClip(clipIndex);
    setEditText(currentText || '');
    setEditInstruction(currentInstruction || '');
    setCardError(null);
  };

  const handleCancelEdit = () => {
    setEditingClip(null);
    setEditText('');
    setEditInstruction('');
  };

  const handleSaveClip = async (clipIndex) => {
    if (!editText.trim()) return;
    setSaving(true);
    setCardError(null);
    try {
      await onUpdateClip(job.jobId, clipIndex, editText, editInstruction);
      setEditingClip(null);
      setEditText('');
      setEditInstruction('');
    } catch (err) {
      setCardError(err.message || 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async (clipIndex) => {
    setDeleting(true);
    setCardError(null);
    try {
      await onDeleteClip(job.jobId, clipIndex);
      setConfirmDeleteClip(null);
    } catch (err) {
      setCardError(err.message || 'Errore eliminazione');
    } finally {
      setDeleting(false);
    }
  };

  const handleConfirmRegenerate = async (clipIndex) => {
    setRegenerating(true);
    setCardError(null);
    try {
      await onRegenerateClip(job.jobId, clipIndex, getDuration(clipIndex));
      setConfirmRegenerateClip(null);
    } catch (err) {
      setCardError(err.message || 'Errore rigenerazione');
    } finally {
      setRegenerating(false);
    }
  };

  const ClipDots = () => (
    <div className="flex items-center gap-1">
      {clips.map((c, i) => {
        if (c.status === 'completed') return (
          <span key={i} className="w-2.5 h-2.5 rounded-full bg-green-500 shrink-0" title={`Clip ${i+1}: completato`} />
        );
        if (c.status === 'in_progress') return (
          <span key={i} className="w-2.5 h-2.5 rounded-full bg-amber-400 shrink-0 animate-pulse" title={`Clip ${i+1}: in generazione`} />
        );
        if (c.status === 'failed') return (
          <span key={i} className="w-2.5 h-2.5 rounded-full bg-red-400 shrink-0" title={`Clip ${i+1}: fallito`} />
        );
        return (
          <span key={i} className="w-2.5 h-2.5 rounded-full border-2 border-gray-300 shrink-0" title={`Clip ${i+1}: in attesa`} />
        );
      })}
    </div>
  );

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
      <div
        className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50"
        onClick={() => setExpanded(e => !e)}
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <span className="text-xs font-mono text-blue-500 shrink-0">{job.jobId}</span>
              {allDone && <CheckCircle2 className="w-3.5 h-3.5 text-green-500 shrink-0" />}
              {hasActive && <Loader className="w-3.5 h-3.5 text-amber-500 animate-spin shrink-0" />}
              {!allDone && !hasActive && failedClips.length > 0 && <AlertCircle className="w-3.5 h-3.5 text-red-500 shrink-0" />}
            </div>
            <p className="text-xs text-gray-500 truncate">"{job.variantTitle}"</p>
          </div>
        </div>

        <div className="flex items-center gap-3 shrink-0 ml-3">
          <ClipDots />
          <span className="text-xs text-gray-400 shrink-0">{completedClips.length}/{clips.length}</span>

          {canSubmitFirst && (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="flex items-center gap-1 px-2.5 py-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-xs font-medium transition-colors"
            >
              <Send className={`w-3 h-3 ${submitting ? 'animate-pulse' : ''}`} />
              {submitting ? 'Invio...' : 'Invia'}
            </button>
          )}

          {canExtend && (
            <button
              onClick={handleExtend}
              disabled={extending}
              className={`flex items-center gap-1 px-2.5 py-1 disabled:opacity-50 text-white rounded-lg text-xs font-medium transition-colors ${nextIsRetry ? 'bg-red-600 hover:bg-red-700' : 'bg-indigo-600 hover:bg-indigo-700'}`}
            >
              <Play className={`w-3 h-3 ${extending ? 'animate-pulse' : ''}`} />
              {extending ? 'Invio...' : nextIsRetry ? `Riprova clip ${nextClipIdx + 1}/${clips.length}` : `Estendi ${nextClipIdx + 1}/${clips.length}`}
            </button>
          )}

          {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-gray-100 px-4 py-3 space-y-4">
          {cardError && (
            <div className="p-2 bg-red-50 rounded text-xs text-red-600">{cardError}</div>
          )}

          {clips.map((c, i) => {
            const jobText        = variantJobs?.[i]?.text        || '';
            const jobInstruction = variantJobs?.[i]?.instruction || '';
            const isEditing  = editingClip === i;
            const wordCount  = (isEditing ? editText : jobText).trim().split(/\s+/).filter(Boolean).length;
            const tooLong    = wordCount > 25;

            return (
              <div key={i} className="border border-gray-100 rounded-lg overflow-hidden">
                {/* Header clip */}
                <div className={`flex items-center justify-between px-3 py-2 text-xs font-medium ${
                  c.status === 'completed' ? 'bg-green-50 text-green-700' :
                  c.status === 'failed'    ? 'bg-red-50 text-red-700' :
                  c.status === 'in_progress' ? 'bg-amber-50 text-amber-700' :
                  'bg-gray-50 text-gray-500'
                }`}>
                  <div className="flex items-center gap-3">
                    <span>Clip {i + 1} di {clips.length}</span>
                    {c.status === 'queued' && (
                      <label className="flex items-center gap-1.5 text-gray-500">
                        <span className="text-gray-400">Durata:</span>
                        <input
                          type="number"
                          min={4}
                          max={8}
                          step={1}
                          value={clipDurations[i] ?? ''}
                          placeholder={suggestDuration(jobText, job.platform)}
                          onClick={e => e.stopPropagation()}
                          onChange={e => {
                            const v = parseInt(e.target.value);
                            setClipDurations(prev => ({ ...prev, [i]: isNaN(v) ? null : Math.min(8, Math.max(4, v)) }));
                          }}
                          className="w-12 text-center text-xs font-mono border border-gray-300 rounded px-1 py-0.5 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-300"
                        />
                        <span className="text-gray-400">s</span>
                        <span className="text-gray-300">(suggerito: {suggestDuration(jobText, job.platform)}s)</span>
                      </label>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`font-mono ${tooLong ? 'text-red-500' : 'text-gray-400'}`}>
                      {wordCount} parole{tooLong ? ' ⚠ >25' : ''}
                    </span>
                    {!isEditing && confirmDeleteClip !== i && confirmRegenerateClip !== i && c.status !== 'in_progress' && (
                      <>
                        {onUpdateClip && (
                          <button
                            onClick={() => handleStartEdit(i, jobText, jobInstruction)}
                            className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-white border border-gray-200 text-gray-500 hover:text-blue-600 hover:border-blue-300 transition-colors"
                          >
                            <Pencil className="w-3 h-3" />
                            Modifica
                          </button>
                        )}
                        {onRegenerateClip && (c.status === 'completed' || c.status === 'failed') && (
                          <button
                            onClick={() => { setConfirmRegenerateClip(i); setEditingClip(null); setConfirmDeleteClip(null); }}
                            className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-white border border-gray-200 text-gray-500 hover:text-amber-600 hover:border-amber-300 transition-colors"
                            title="Rigenera clip"
                          >
                            <RotateCcw className="w-3 h-3" />
                          </button>
                        )}
                        {onDeleteClip && (
                          <button
                            onClick={() => { setConfirmDeleteClip(i); setEditingClip(null); setConfirmRegenerateClip(null); }}
                            className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-white border border-gray-200 text-gray-500 hover:text-red-600 hover:border-red-300 transition-colors"
                            title="Elimina clip"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </>
                    )}
                    {confirmRegenerateClip === i && (
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-xs text-amber-600 font-medium">Rigenerare?</span>
                        <label className="flex items-center gap-1 text-xs text-gray-500">
                          <span>Durata:</span>
                          <input
                            type="number"
                            min={4}
                            max={8}
                            step={1}
                            value={clipDurations[i] ?? ''}
                            placeholder={suggestDuration(variantJobs?.[i]?.text, job.platform)}
                            onClick={e => e.stopPropagation()}
                            onChange={e => {
                              const v = parseInt(e.target.value);
                              setClipDurations(prev => ({ ...prev, [i]: isNaN(v) ? null : Math.min(8, Math.max(4, v)) }));
                            }}
                            className="w-10 text-center text-xs font-mono border border-amber-300 rounded px-1 py-0.5 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-amber-300"
                          />
                          <span>s</span>
                        </label>
                        <button
                          onClick={() => setConfirmRegenerateClip(null)}
                          disabled={regenerating}
                          className="px-1.5 py-0.5 rounded border border-gray-200 bg-white text-gray-500 text-xs hover:bg-gray-50 transition-colors"
                        >
                          No
                        </button>
                        <button
                          onClick={() => handleConfirmRegenerate(i)}
                          disabled={regenerating}
                          className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white text-xs font-medium transition-colors"
                        >
                          {regenerating ? <Loader className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                          {regenerating ? '...' : 'Sì, rigenera'}
                        </button>
                      </div>
                    )}
                    {confirmDeleteClip === i && (
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs text-red-600 font-medium">Eliminare?</span>
                        <button
                          onClick={() => setConfirmDeleteClip(null)}
                          disabled={deleting}
                          className="px-1.5 py-0.5 rounded border border-gray-200 bg-white text-gray-500 text-xs hover:bg-gray-50 transition-colors"
                        >
                          No
                        </button>
                        <button
                          onClick={() => handleConfirmDelete(i)}
                          disabled={deleting}
                          className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-xs font-medium transition-colors"
                        >
                          {deleting ? <Loader className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                          {deleting ? '...' : 'Sì, elimina'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Testo / editor */}
                <div className="px-3 py-2">
                  {isEditing ? (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Testo parlato</label>
                        <textarea
                          value={editText}
                          onChange={e => setEditText(e.target.value)}
                          rows={4}
                          className="w-full text-sm text-gray-800 border border-blue-300 rounded-lg p-2 resize-none focus:outline-none focus:ring-2 focus:ring-blue-200"
                          placeholder="Testo del dialogo..."
                        />
                        <span className={`text-xs ${tooLong ? 'text-red-500 font-semibold' : 'text-gray-400'}`}>
                          {wordCount} parole {tooLong ? '— troppo per 8s, riduci' : '— ok per VEO'}
                        </span>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Istruzione regia</label>
                        <textarea
                          value={editInstruction}
                          onChange={e => setEditInstruction(e.target.value)}
                          rows={3}
                          className="w-full text-sm text-gray-800 border border-blue-300 rounded-lg p-2 resize-none focus:outline-none focus:ring-2 focus:ring-blue-200"
                          placeholder="Istruzione per il rendering..."
                        />
                      </div>
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={handleCancelEdit}
                          disabled={saving}
                          className="flex items-center gap-1 px-2.5 py-1 rounded-lg border border-gray-200 text-gray-500 text-xs hover:bg-gray-50 transition-colors"
                        >
                          <X className="w-3 h-3" /> Annulla
                        </button>
                        <button
                          onClick={() => handleSaveClip(i)}
                          disabled={saving || !editText.trim()}
                          className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs font-medium transition-colors"
                        >
                          {saving ? <Loader className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                          {saving ? 'Salvo...' : 'Salva e resetta'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-xs text-gray-700 leading-relaxed whitespace-pre-wrap">{jobText || <span className="italic text-gray-400">Testo non disponibile</span>}</p>
                      {jobInstruction && (
                        <p className="text-xs text-gray-400 italic leading-relaxed whitespace-pre-wrap border-t border-gray-100 pt-2">{jobInstruction}</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Video completato */}
                {c.video?.uri && (
                  <div className="px-3 pb-3">
                    <VeoVideoPlayer uri={c.video.uri} mimeType={c.video.mimeType} label={`clip ${i + 1}`} />
                  </div>
                )}

                {/* Errore clip */}
                {c.status === 'failed' && c.error && (
                  <div className="mx-3 mb-3 p-2 bg-red-50 rounded text-xs text-red-600">
                    {c.error}
                  </div>
                )}

                {/* Operation name */}
                {c.operationName && (
                  <p className="px-3 pb-2 text-xs text-gray-400 font-mono break-all">{c.operationName}</p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Colori renderer ─────────────────────────────────────────────────────────

const RENDERER_COLORS = {
  veo:        { from: 'from-blue-600',   to: 'to-cyan-600'   },
  kling:      { from: 'from-orange-500', to: 'to-red-600'    },
  runway:     { from: 'from-purple-600', to: 'to-pink-600'   },
  seedance:   { from: 'from-green-600',  to: 'to-teal-600'   },
  higgsfield: { from: 'from-indigo-600', to: 'to-violet-700' },
  hailuo:     { from: 'from-sky-600',    to: 'to-blue-700'   },
};

// ─── CollapsibleJsonCard ──────────────────────────────────────────────────────

const CollapsibleJsonCard = ({ title, icon: Icon, iconColor, borderColor, bgColor, data }) => {
  const [open, setOpen] = useState(false);
  const isEmpty = !data || Object.keys(data).length === 0;
  return (
    <div className={`border ${borderColor} rounded-xl overflow-hidden`}>
      <button
        onClick={() => setOpen(o => !o)}
        className={`w-full flex items-center justify-between px-4 py-3 ${bgColor} hover:opacity-90 transition-opacity`}
      >
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${iconColor}`} />
          <span className={`font-semibold text-sm ${iconColor}`}>{title}</span>
          {isEmpty && <span className="text-xs opacity-50 italic">(empty)</span>}
        </div>
        {open ? <ChevronUp className={`w-4 h-4 ${iconColor} opacity-50`} /> : <ChevronDown className={`w-4 h-4 ${iconColor} opacity-50`} />}
      </button>
      {open && (
        <div className="bg-gray-950 p-4 overflow-x-auto">
          <pre className="text-xs text-green-300 font-mono whitespace-pre-wrap break-all leading-relaxed">
            {JSON.stringify(data, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

// ─── Normalizzazione struttura variants ──────────────────────────────────────
// Gestisce sia Structure A (variants[]) che Structure B (render_jobs[] flat).

const normalizeVariants = (generationOptions) => {
  if (!generationOptions) return [];

  // Structure A / A': variants[] presenti — normalizza i nomi dei campi
  if (generationOptions.variants?.length > 0) {
    return generationOptions.variants.map(v => ({
      variant_id:          v.variant_id || v.script_id || '',
      title:               v.title || v.variant_title || v.variant_id || v.script_id || '',
      narrative_character: v.narrative_character || v.speaker_role || v.jobs?.[0]?.narrative_character || '',
      setting:             v.setting || v.jobs?.[0]?.setting || '',
      jobs: (v.jobs || []).map((j, idx) => ({
        job_id:      j.job_id || `${v.variant_id}_${idx + 1}`,
        order:       j.order ?? j.relative_order ?? (idx + 1),
        text:        j.text || j.spoken_text || j.spoken_dialogue_exact || j.spoken_sequence || j.spoken_line || j.authoritative_text || j.spoken_dialogue || j.dialogue || '',
        instruction: j.instruction || j.render_instruction || j.setting || ''
      }))
    }));
  }

  // Structure B: flat render_jobs[] — raggruppa per variant_id
  const renderJobs = generationOptions.render_jobs || [];
  if (renderJobs.length === 0) return [];

  const variantMap = new Map();
  for (const rj of renderJobs) {
    const vid = rj.variant_id || rj.script_id || 'unknown';
    if (!variantMap.has(vid)) {
      variantMap.set(vid, {
        variant_id:          vid,
        title:               rj.variant_title || vid,
        narrative_character: rj.speaker_role || rj.narrative_character || '',
        setting:             rj.setting      || '',
        jobs:                []
      });
    }
    const current = variantMap.get(vid);
    current.jobs.push({
      job_id:      rj.job_id,
      order:       rj.relative_order ?? rj.order ?? (current.jobs.length + 1),
      text:        rj.authoritative_text || rj.text || rj.spoken_dialogue_exact || rj.spoken_line || rj.spoken_dialogue || rj.dialogue || '',
      instruction: rj.render_instruction || rj.instruction || rj.setting || ''
    });
  }

  for (const v of variantMap.values()) {
    v.jobs.sort((a, b) => a.order - b.order);
  }

  return Array.from(variantMap.values());
};

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────

const Contract164View = ({
  execution, onApprove, onSubmitVeoJob, onExtendVeoJob, onUpdateVeoClip, onDeleteVeoClip, onRegenerateVeoClip, onCheckVeoStatus,
  onDiscardVeo, onResubmitVeo, onRebuildJobs, onReload, loading
}) => {
  const inp = execution?.inputData || {};
  const out = execution?.outputData || {};

  const renderer     = inp.targetRenderer || {};
  const rendererName = (renderer.name || 'veo').toUpperCase();
  const colors       = RENDERER_COLORS[renderer.name?.toLowerCase()] || { from: 'from-gray-600', to: 'to-gray-700' };

  const variants  = normalizeVariants(inp.backendPayload?.generation_options);
  const totalJobs = variants.reduce((s, v) => s + (v.jobs?.length || 0), 0);

  const [selectedVariants, setSelectedVariants]   = useState(() => variants.map(v => v.variant_id));
  const [variantImages, setVariantImages]         = useState({});
  const [variantPlatforms, setVariantPlatforms]   = useState({});
  const [veoChecking, setVeoChecking]             = useState(false);
  const [veoStatus, setVeoStatus]               = useState(null);
  const [discarding, setDiscarding]             = useState(false);
  const [rebuilding, setRebuilding]             = useState(false);
  const [reloading, setReloading]               = useState(false);

  useEffect(() => {
    setSelectedVariants(variants.map(v => v.variant_id));
    setVariantImages({});
    setVariantPlatforms({});
  }, [execution?._id]);

  const handleSelectImage = (variantId, uri) => {
    setVariantImages(prev => ({ ...prev, [variantId]: uri }));
  };

  const handleSelectPlatform = (variantId, platform) => {
    setVariantPlatforms(prev => ({ ...prev, [variantId]: platform }));
  };

  const toggleVariant = (variantId) => {
    setSelectedVariants(prev =>
      prev.includes(variantId) ? prev.filter(id => id !== variantId) : [...prev, variantId]
    );
  };

  const selectedJobs = variants
    .filter(v => selectedVariants.includes(v.variant_id))
    .reduce((s, v) => s + (v.jobs?.length || 0), 0);

  const handleApprove = () => {
    if (selectedVariants.length === 0) return;
    onApprove({ confirmed: true, selectedVariants, variantImages, variantPlatforms });
  };

  const handleCheckVeo = async () => {
    setVeoChecking(true);
    setVeoStatus(null);
    try {
      const result = await onCheckVeoStatus();
      setVeoStatus(result);
    } catch (e) {
      setVeoStatus({ error: e.message });
    } finally {
      setVeoChecking(false);
    }
  };

  const handleDiscard = async () => {
    setDiscarding(true);
    try {
      await onDiscardVeo();
      setVeoStatus(null);
    } catch (e) {
      setVeoStatus({ error: e.message });
    } finally {
      setDiscarding(false);
    }
  };

  const handleRebuildJobs = async () => {
    setRebuilding(true);
    setVeoStatus(null);
    try {
      await onRebuildJobs();
    } catch (e) {
      setVeoStatus({ error: e.message });
    } finally {
      setRebuilding(false);
    }
  };

  const handleReload = async () => {
    setReloading(true);
    try {
      await onReload();
    } finally {
      setReloading(false);
    }
  };

  // ── STATO APPROVED ─────────────────────────────────────────────────────────
  if (execution?.status === 'approved') {
    const veoJobs          = out.veo_jobs || [];
    const approvedVariants = normalizeVariants(inp.backendPayload?.generation_options);
    const allClips = veoJobs.flatMap(j => j.clips || []);

    const totalClipCount     = allClips.length;
    const completedClipCount = allClips.filter(c => c.status === 'completed').length;
    const inProgressCount    = allClips.filter(c => c.status === 'in_progress').length;
    const queuedClipCount    = allClips.filter(c => c.status === 'queued').length;
    const failedClipCount    = allClips.filter(c => c.status === 'failed').length;
    const completedVariants  = veoJobs.filter(j => j.status === 'completed').length;

    return (
      <div className="h-full overflow-y-auto">
        <div className="p-6 max-w-2xl mx-auto space-y-4">

          {/* Riepilogo stato */}
          <div className="bg-green-50 border border-green-200 rounded-xl p-5">
            <div className="flex items-center gap-2 text-green-700 mb-3">
              <CheckCircle2 className="w-5 h-5" />
              <span className="font-semibold">
                {completedVariants}/{veoJobs.length} varianti — {completedClipCount}/{totalClipCount} clip
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {queuedClipCount > 0 && (
                <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs font-medium">
                  {queuedClipCount} clip in attesa
                </span>
              )}
              {inProgressCount > 0 && (
                <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full text-xs font-medium">
                  {inProgressCount} clip in generazione
                </span>
              )}
              {completedClipCount > 0 && (
                <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-xs font-medium">
                  {completedClipCount} clip completati
                </span>
              )}
              {failedClipCount > 0 && (
                <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded-full text-xs font-medium">
                  {failedClipCount} clip falliti
                </span>
              )}
            </div>
          </div>

          {/* Card per variante */}
          {veoJobs.map(job => {
            const matchedVariant = approvedVariants.find(v => v.variant_id === job.jobId);
            return (
            <VariantResultCard
              key={job.jobId}
              job={job}
              variantJobs={matchedVariant?.jobs || []}
              onSubmitJob={onSubmitVeoJob}
              onExtendJob={onExtendVeoJob}
              onUpdateClip={onUpdateVeoClip}
              onDeleteClip={onDeleteVeoClip}
              onRegenerateClip={onRegenerateVeoClip}
            />
          );
          })}

          {/* Banner nessun job — exec approvata senza lista */}
          {veoJobs.length === 0 && onRebuildJobs && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-amber-800">Lista job non inizializzata</p>
                <p className="text-xs text-amber-700 mt-0.5">
                  L&apos;esecuzione è stata approvata ma la lista dei job VEO non è stata generata.
                  Clicca per ricostruirla dai dati di C16.3.
                </p>
              </div>
              <button
                onClick={handleRebuildJobs}
                disabled={rebuilding || loading}
                className="shrink-0 px-3 py-1.5 bg-amber-600 text-white text-xs font-semibold rounded-lg hover:bg-amber-700 disabled:opacity-50 flex items-center gap-1.5 transition-colors"
              >
                {rebuilding ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                {rebuilding ? 'Ricostruzione...' : 'Ricostruisci job list'}
              </button>
            </div>
          )}

          {/* Azioni globali */}
          <div className="flex flex-col gap-3 pt-2">
            {inProgressCount > 0 && (
              <button
                onClick={handleCheckVeo}
                disabled={veoChecking}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                <RefreshCw className={`w-4 h-4 ${veoChecking ? 'animate-spin' : ''}`} />
                {veoChecking ? 'Controllo in corso...' : `Controlla stato (${inProgressCount} clip in corso)`}
              </button>
            )}

            {completedClipCount > 0 && (
              <button
                onClick={handleDiscard}
                disabled={discarding}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-white border border-red-200 text-red-600 rounded-xl font-medium hover:bg-red-50 disabled:opacity-50 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                {discarding ? 'Scarto...' : `Scarta video (${completedClipCount} clip)`}
              </button>
            )}
          </div>

          {/* Feedback check / errori */}
          {veoStatus?.error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
              {veoStatus.error}
            </div>
          )}
          {veoStatus?.summary && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-700">
              Aggiornato: {veoStatus.summary.completed} completati, {veoStatus.summary.in_progress} in corso, {veoStatus.summary.failed} falliti.
            </div>
          )}

        </div>
      </div>
    );
  }

  // ── STATO PENDING (conferma prima dell'invio) ───────────────────────────────
  const generationPrompt = inp.backendPrompt?.generation_prompt || inp.backendPayload?.prompt || null;
  const negativePrompt   = inp.backendPrompt?.negative_prompt   || null;

  return (
    <div className="h-full overflow-y-auto">
      <div className="space-y-4 p-6 max-w-3xl mx-auto">

        {/* Header renderer */}
        <div className={`bg-gradient-to-r ${colors.from} ${colors.to} rounded-xl p-5 text-white`}>
          <div className="flex items-center gap-2 mb-1">
            <Send className="w-5 h-5 opacity-80" />
            <span className="text-sm font-medium opacity-80 uppercase tracking-wide">Conferma invio</span>
          </div>
          <h2 className="text-xl font-bold">{rendererName} 3.1</h2>
          <p className="text-sm opacity-75 mt-1">
            Seleziona le varianti da inviare. Ogni variante sarà generata in clip sequenziali (max 8s ciascuno).
          </p>
          <div className="grid grid-cols-3 gap-3 mt-4 text-sm">
            <div className="bg-white/10 rounded-lg p-2">
              <div className="opacity-70 text-xs mb-0.5">Varianti</div>
              <div className="font-semibold">{variants.length}</div>
            </div>
            <div className="bg-white/10 rounded-lg p-2">
              <div className="opacity-70 text-xs mb-0.5">Clip totali</div>
              <div className="font-semibold">{totalJobs}</div>
            </div>
            <div className="bg-white/10 rounded-lg p-2">
              <div className="opacity-70 text-xs mb-0.5">Image refs</div>
              <div className="font-semibold">{inp.imageReferences?.length || inp.backendPayload?.image_references?.length || 0}</div>
            </div>
          </div>
        </div>

        {/* Banner dati vuoti */}
        {variants.length === 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-800">Dati non ancora caricati da C16.3</p>
              <p className="text-xs text-amber-700 mt-0.5">
                Il payload VEO non è disponibile. Clicca per caricare i dati dall&apos;output approvato di C16.3.
              </p>
            </div>
            {onReload && (
              <button
                onClick={handleReload}
                disabled={reloading || loading}
                className="shrink-0 px-3 py-1.5 bg-amber-600 text-white text-xs font-semibold rounded-lg hover:bg-amber-700 disabled:opacity-50 flex items-center gap-1.5 transition-colors"
              >
                {reloading ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                {reloading ? 'Caricamento...' : 'Ricarica da C16.3'}
              </button>
            )}
          </div>
        )}

        {/* Negative Prompt */}
        {negativePrompt && (
          <div className="bg-red-50 border border-red-100 rounded-xl p-4">
            <span className="text-xs font-semibold text-red-600 uppercase tracking-wide">Negative Prompt</span>
            <p className="text-sm text-red-700 leading-relaxed mt-1 line-clamp-3">{negativePrompt}</p>
          </div>
        )}

        {/* Selezione varianti */}
        {variants.length > 0 ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-sm text-gray-800">Seleziona varianti da inviare</h3>
              <div className="flex gap-2 text-xs">
                <button onClick={() => setSelectedVariants(variants.map(v => v.variant_id))} className="text-blue-600 hover:underline">Tutte</button>
                <button onClick={() => setSelectedVariants([])} className="text-gray-400 hover:underline">Nessuna</button>
              </div>
            </div>
            {variants.map(variant => (
              <VariantCard
                key={variant.variant_id}
                variant={variant}
                selected={selectedVariants.includes(variant.variant_id)}
                onToggle={toggleVariant}
                imageReferences={inp.imageReferences || inp.backendPayload?.image_references || []}
                selectedImage={variantImages[variant.variant_id] || null}
                onSelectImage={handleSelectImage}
                selectedPlatform={variantPlatforms[variant.variant_id] || null}
                onSelectPlatform={handleSelectPlatform}
              />
            ))}
          </div>
        ) : (
          generationPrompt && (
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <Film className="w-4 h-4 text-blue-500" />
                <span className="font-semibold text-sm text-gray-800">Generation Prompt</span>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap bg-gray-50 rounded-lg p-4 border border-gray-100">
                {generationPrompt}
              </p>
            </div>
          )
        )}

        {/* Dettagli tecnici collassabili */}
        <CollapsibleJsonCard
          title="Backend Payload"
          icon={Zap}
          iconColor="text-blue-600"
          borderColor="border-blue-200"
          bgColor="bg-blue-50"
          data={inp.backendPayload}
        />
        <CollapsibleJsonCard
          title="Backend Prompt"
          icon={FileCode}
          iconColor="text-purple-600"
          borderColor="border-purple-200"
          bgColor="bg-purple-50"
          data={inp.backendPrompt}
        />
        <CollapsibleJsonCard
          title="Backend Parameters"
          icon={Settings}
          iconColor="text-teal-600"
          borderColor="border-teal-200"
          bgColor="bg-teal-50"
          data={inp.backendParameters}
        />

        {/* Pulsante conferma */}
        <div className="flex justify-end pt-2 pb-4">
          <button
            onClick={handleApprove}
            disabled={loading || selectedVariants.length === 0}
            className={`bg-gradient-to-r ${colors.from} ${colors.to} hover:opacity-90 disabled:opacity-50 text-white px-6 py-3 rounded-xl font-semibold flex items-center gap-2 transition-opacity shadow-md`}
          >
            <Send className="w-4 h-4" />
            {loading
              ? 'Conferma in corso...'
              : `Conferma — ${selectedVariants.length} ${selectedVariants.length === 1 ? 'variante' : 'varianti'}, ${selectedJobs} clip`}
          </button>
        </div>

      </div>
    </div>
  );
};

export default Contract164View;
